import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Volume2, VolumeX, Activity } from 'lucide-react';

const MAX_RETRIES = 100;
const RETRY_BASE_MS = 1500;

// Safari/WebKit detection — computed once at module load
// Used to disable canvas options that cause issues on iOS Safari
const IS_WEBKIT = /AppleWebKit/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

// Pre-compiled regex patterns for overlay timestamp tokens — created once at module load
const TIME_PATTERNS = {
    '%Y': /%Y/g,
    '%m': /%m/g,
    '%d': /%d/g,
    '%H': /%H/g,
    '%M': /%M/g,
    '%S': /%S/g,
};

/**
 * Parse raw NALU bytes to extract the WebCodecs avc1.PPCCLL codec string
 * from the H.264 SPS NAL unit (type 7).
 *
 * A keyframe packet typically contains:
 *   SPS (type 7) → PPS (type 8) → IDR slice (type 5)
 * separated by 3- or 4-byte start codes (0x00 0x00 0x01 or 0x00 0x00 0x00 0x01).
 *
 * The first three payload bytes of an SPS unit are:
 *   profile_idc, constraint_flags, level_idc
 * which map directly to the avc1.PPCCLL codec string.
 */
function detectCodecFromSPS(naluBytes) {
    const buf = new Uint8Array(naluBytes);
    let i = 0;
    while (i < buf.length - 5) {
        // Find 3-byte start code
        if (buf[i] === 0x00 && buf[i + 1] === 0x00 && buf[i + 2] === 0x01) {
            const nalHeader = buf[i + 3];
            const nalType = nalHeader & 0x1f;
            // SPS is NAL type 7
            if (nalType === 7 && buf.length > i + 6) {
                const profileIdc = buf[i + 4];
                const constraintFlags = buf[i + 5];
                const levelIdc = buf[i + 6];
                const codec = `avc1.${profileIdc.toString(16).padStart(2, '0')}${constraintFlags.toString(16).padStart(2, '0')}${levelIdc.toString(16).padStart(2, '0')}`;
                console.debug(`[WebCodecs] SPS detected → codec=${codec} (profile=${profileIdc}, level=${levelIdc})`);
                return codec;
            }
            i += 4; // skip past this NAL header
        } else {
            i++;
        }
    }
    return null; // SPS not found in this packet
}

/**
 * G.711 A-law to 16-bit PCM decoder
 */
function alaw2linear(alaw) {
    alaw ^= 0x55;
    let sign = (alaw & 0x80) ? -1 : 1;
    let exponent = (alaw & 0x70) >> 4;
    let mantissa = alaw & 0x0f;
    let sample = 0;
    if (exponent === 0) {
        sample = (mantissa << 4) + 8;
    } else {
        sample = (mantissa << 4) + 0x108;
        sample <<= (exponent - 1);
    }
    return (sign * sample) / 32768.0; // Normalize to -1.0..1.0 for Web Audio
}

export const WebCodecsPlayer = ({ camera, onStateChange, videoEnabled = true, isAuditing, quality = 'main' }) => {
    const { token } = useAuth();
    const cameraId = camera?.id;
    const canvasRef = useRef(null);
    const wsRef = useRef(null);
    const decoderRef = useRef(null);
    const isReadyRef = useRef(false);
    const configuredCodecRef = useRef(null);  // tracks currently configured codec string
    const pendingFramesRef = useRef([]);
    const retryCountRef = useRef(0);
    const retryTimerRef = useRef(null);
    const animFrameRef = useRef(null);
    const watchdogTimerRef = useRef(null);
    const watchdogIntervalRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    const lastFrameTimeRef = useRef(0);
    const isMountedRef = useRef(true);
    const latestMetadataRef = useRef([]);
    const metadataTimeRef = useRef(0);
    const overlayTimeRef = useRef({ sec: -1, values: null }); // cached time strings, recomputed once per second
    const wsOpenTimeRef = useRef(0);            // when the current WS connection was opened
    const lastDecodedFrameTimeRef = useRef(0);  // when the decoder last emitted a frame
    const decoderErrorCountRef = useRef(0);     // consecutive decoder errors → JPEG fallback

    // Audio Refs
    const audioCtxRef = useRef(null);
    const audioStartTimeRef = useRef(0);
    const isAuditingRef = useRef(isAuditing); // Use ref to avoid stale closure in WS handler
    const [isMuted, setIsMuted] = useState(true);

    // Sync ref with prop
    useEffect(() => {
        isAuditingRef.current = isAuditing;
    }, [isAuditing]);

    const [status, setStatus] = useState('connecting');

    useEffect(() => {
        if (onStateChange) onStateChange(status);
    }, [status, onStateChange]);

    const drawOverlay = useCallback((ctx, w, h) => {
        if (!camera) return;

        const userPreference = camera.text_scale || 1.0;
        const fontScale = Math.max(0.4, (w / 1200.0) * userPreference);
        const thickness = Math.max(1, Math.floor(fontScale * 2.0));

        // OpenCV FONT_HERSHEY_SIMPLEX base size at scale 1.0 is visually larger than 24px.
        // Increasing to 30px base to match the user's reported discrepancy.
        const fontSize = Math.floor(30 * fontScale);

        // Simplex is a thin stroke font. 500 for thickness 1, 700 for thickness 2+.
        ctx.font = `${thickness > 1 ? '700' : '500'} ${fontSize}px sans-serif`;

        const processText = (text) => {
            if (!text) return "";
            let processed = text.replace(/%\$/g, camera.name || '').replace(/%N/g, camera.name || '');
            if (processed.includes('%')) {
                // Recompute time strings at most once per second
                const nowSec = Math.floor(Date.now() / 1000);
                if (overlayTimeRef.current.sec !== nowSec) {
                    const now = new Date(nowSec * 1000);
                    const pad = (n) => String(n).padStart(2, '0');
                    overlayTimeRef.current = {
                        sec: nowSec,
                        values: {
                            '%Y': String(now.getFullYear()),
                            '%m': pad(now.getMonth() + 1),
                            '%d': pad(now.getDate()),
                            '%H': pad(now.getHours()),
                            '%M': pad(now.getMinutes()),
                            '%S': pad(now.getSeconds()),
                        }
                    };
                }
                const vals = overlayTimeRef.current.values;
                Object.keys(vals).forEach(key => {
                    processed = processed.replace(TIME_PATTERNS[key], vals[key]);
                });
            }
            return processed;
        };

        // Text Left (Top Left)
        const textLeft = processText(camera.text_left || "");
        if (textLeft) {
            const metrics = ctx.measureText(textLeft);
            const textWidth = metrics.width;
            const textHeight = fontSize;
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, textWidth + 20, textHeight + 20);
            ctx.fillStyle = 'white';
            ctx.textBaseline = 'top';
            ctx.fillText(textLeft, 10, 10);
        }

        // Text Right (Bottom Right)
        const textRight = processText(camera.text_right || "");
        if (textRight) {
            const metrics = ctx.measureText(textRight);
            const textWidth = metrics.width;
            const textHeight = fontSize;
            ctx.fillStyle = 'black';
            ctx.fillRect(w - textWidth - 20, h - textHeight - 20, textWidth + 20, textHeight + 20);
            ctx.fillStyle = 'white';
            ctx.textBaseline = 'alphabetic';
            ctx.fillText(textRight, w - textWidth - 10, h - 10);
        }
        // Depend on the specific overlay fields only — NOT the whole camera object.
        // A new camera object on every parent re-render (health polling) must not
        // ripple through scheduleRender → connect and force a WebSocket reconnect.
    }, [camera?.text_scale, camera?.name, camera?.text_left, camera?.text_right]);

    const drawAIBoxes = useCallback((ctx, w, h) => {
        const metadata = latestMetadataRef.current;
        const now = Date.now();
        
        // Only draw if metadata is fresh (< 1.5s) to avoid ghosts on stream freeze
        if (!metadata || metadata.length === 0 || (now - metadataTimeRef.current > 1500)) {
            return;
        }

        metadata.forEach(res => {
            try {
                const label = res.label || 'unknown';
                const conf = res.confidence || 0.0;
                const box = res.box || [0, 0, 0, 0]; // [ymin, xmin, ymax, xmax]
                
                // Colors (match Engine's CameraThread colors)
                let color = '#00FF00'; // Default Green
                if (label === 'person') color = '#00FF00';
                else if (['vehicle', 'car', 'bus', 'truck'].includes(label)) color = '#0000FF';
                else if (['dog', 'cat', 'bird'].includes(label)) color = '#FFA500';
                
                const [ymin, xmin, ymax, xmax] = box;
                const x1 = xmin * w;
                const y1 = ymin * h;
                const x2 = xmax * w;
                const y2 = ymax * h;
                const bw = x2 - x1;
                const bh = y2 - y1;

                ctx.strokeStyle = color;
                ctx.lineWidth = Math.max(2, w / 400);
                ctx.strokeRect(x1, y1, bw, bh);

                // Label
                const text = `${label.charAt(0).toUpperCase() + label.slice(1)} ${Math.round(conf * 100)}%`;
                const fontScale = Math.max(0.4, w / 1200);
                const fontSize = Math.floor(20 * fontScale);
                ctx.font = `bold ${fontSize}px sans-serif`;
                
                const metrics = ctx.measureText(text);
                const tw = metrics.width;
                const th = fontSize;
                const padding = 4;

                ctx.fillStyle = color;
                let labelY = y1 - th - (padding * 2);
                if (labelY < 0) labelY = y1;

                ctx.fillRect(x1, labelY, tw + (padding * 2), th + (padding * 2));
                
                ctx.fillStyle = 'black';
                ctx.textBaseline = 'top';
                ctx.fillText(text, x1 + padding, labelY + padding);
            } catch (e) {
                console.warn('[WebCodecs] Error drawing box:', e);
            }
        });
    }, []);

    // ── Render loop ───────────────────────────────────────────────────────────
    const scheduleRender = useCallback(() => {
        if (animFrameRef.current) return;
        animFrameRef.current = requestAnimationFrame(() => {
            animFrameRef.current = null;
            if (!isMountedRef.current || !canvasRef.current) {
                while (pendingFramesRef.current.length > 0) {
                    try { pendingFramesRef.current.shift().close(); } catch (_) { }
                }
                return;
            }
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d', { alpha: false, desynchronized: !IS_WEBKIT });
            const frames = pendingFramesRef.current;
            if (frames.length === 0) return;

            const frame = frames.shift();
            // DROP LOGIC: If we have a backlog (latency building up), 
            // drop EVERYTHING except a tiny cushion to stay near the "present"
            // while allowing for a little network jitter.
            const jitterBufferTarget = 2;
            if (frames.length > jitterBufferTarget) {
                // Keep the most recent frames, close the stale ones
                const staleCount = frames.length - jitterBufferTarget;
                const staleFrames = frames.splice(0, staleCount);
                for (const f of staleFrames) {
                    try { f.close(); } catch (_) { }
                }
            }

            const rotation = camera.rotation || 0;
            const drawW = frame.displayWidth;
            const drawH = frame.displayHeight;

            // Resize canvas to match the expected rotated output
            if (rotation === 90 || rotation === 270) {
                if (canvas.width !== drawH || canvas.height !== drawW) {
                    canvas.width = drawH;
                    canvas.height = drawW;
                    canvas.style.aspectRatio = `${drawH} / ${drawW}`;
                }
            } else {
                if (canvas.width !== drawW || canvas.height !== drawH) {
                    canvas.width = drawW;
                    canvas.height = drawH;
                    canvas.style.aspectRatio = `${drawW} / ${drawH}`;
                }
            }

            ctx.save();
            // Handle rotation
            if (rotation === 90) {
                ctx.translate(canvas.width, 0);
                ctx.rotate(Math.PI / 2);
            } else if (rotation === 180) {
                ctx.translate(canvas.width, canvas.height);
                ctx.rotate(Math.PI);
            } else if (rotation === 270) {
                ctx.translate(0, canvas.height);
                ctx.rotate(-Math.PI / 2);
            }

            ctx.drawImage(frame, 0, 0, drawW, drawH);
            ctx.restore();

            // Draw AI Bounding Boxes from metadata
            drawAIBoxes(ctx, canvas.width, canvas.height);

            // Draw Client-side Overlay on top of the final rotated canvas
            drawOverlay(ctx, canvas.width, canvas.height);

            try { frame.close(); } catch (_) { }
        });
    }, [drawOverlay, camera.rotation]);

    // ── Cleanup helpers ───────────────────────────────────────────────────────
    // IMPORTANT: closeWS and closeDecoder MUST be declared before createDecoder
    // because createDecoder references them in its dependency array and body.
    // Placing them after createDecoder causes a Temporal Dead Zone error in
    // production builds (the minified const is accessed before initialization).
    const closeWS = useCallback(() => {
        if (!wsRef.current) return;
        const ws = wsRef.current;
        wsRef.current = null;
        ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
        try { ws.close(); } catch (_) { }
    }, []);

    const closeDecoder = useCallback(() => {
        if (decoderRef.current && decoderRef.current.state !== 'closed') {
            try { decoderRef.current.close(); } catch (_) { }
        }
        decoderRef.current = null;
        configuredCodecRef.current = null;
        isReadyRef.current = false;
        while (pendingFramesRef.current.length > 0) {
            try { pendingFramesRef.current.shift().close(); } catch (_) { }
        }
    }, []);

    // ── Decoder factory ───────────────────────────────────────────────────────
    // Returns a fresh, UNconfigured VideoDecoder. Configuration is deferred
    // until the first keyframe arrives and we know the real codec profile.
    const createDecoder = useCallback(() => {
        if (!('VideoDecoder' in window)) return null;
        try {
            const dec = new VideoDecoder({
                output: (frame) => {
                    if (!isMountedRef.current) { try { frame.close(); } catch (_) { } return; }
                    if (!videoEnabled) { try { frame.close(); } catch (_) { } return; }
                    lastDecodedFrameTimeRef.current = Date.now();
                    decoderErrorCountRef.current = 0; // successful frame resets the error counter
                    pendingFramesRef.current.push(frame);
                    scheduleRender();
                    setStatus(prev => prev !== 'loaded' ? 'loaded' : prev);
                },
                error: (e) => {
                    console.error('[WebCodecs] Decoder error:', e);
                    decoderErrorCountRef.current += 1;
                    // After 5 consecutive decoder errors the codec is unsupported on this
                    // device (common on iOS Safari with High Profile H.264). Fall back to JPEG.
                    if (decoderErrorCountRef.current >= 5) {
                        console.warn('[WebCodecs] Too many decoder errors — falling back to JPEG.');
                        setStatus('error');
                        closeWS();
                        closeDecoder();
                        return;
                    }
                    // Force re-detection on the next keyframe
                    configuredCodecRef.current = null;
                    isReadyRef.current = false;
                },
            });
            return dec;
        } catch (e) {
            console.error('[WebCodecs] Failed to create VideoDecoder:', e);
            return null;
        }
    }, [scheduleRender, closeWS, closeDecoder]);

    // Configure (or reconfigure) the decoder for a given codec string.
    // Creates a new decoder instance if needed (state machine can't go back to 'unconfigured').
    const ensureDecoderConfigured = useCallback((codec) => {
        if (configuredCodecRef.current === codec && decoderRef.current?.state === 'configured') {
            return true; // already configured correctly
        }

        // Close existing decoder if open
        if (decoderRef.current && decoderRef.current.state !== 'closed') {
            try { decoderRef.current.close(); } catch (_) { }
        }
        // Drain pending frames from old decoder
        while (pendingFramesRef.current.length > 0) {
            try { pendingFramesRef.current.shift().close(); } catch (_) { }
        }

        const dec = createDecoder();
        if (!dec) return false;
        decoderRef.current = dec;

        // Try detected codec first, then Baseline as iOS-safe fallback.
        // configure() is synchronous — isTypeSupported() (async) is called
        // proactively in connect() before the first frame arrives.
        const tryCodecs = codec !== 'avc1.42E01E'
            ? [codec, 'avc1.42E01E']
            : ['avc1.42E01E'];

        for (const c of tryCodecs) {
            try {
                dec.configure({ codec: c, optimizeForLatency: true });
                configuredCodecRef.current = c;
                console.debug(`[WebCodecs] Decoder configured with codec: ${c}`);
                return true;
            } catch (e) {
                console.warn(`[WebCodecs] configure(${c}) failed:`, e);
            }
        }
        console.error('[WebCodecs] No supported codec found for this device.');
        return false;
    }, [createDecoder]);

    const initAudio = useCallback(() => {
        if (!audioCtxRef.current) {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return; // browser has no Web Audio at all
            audioCtxRef.current = new AudioCtx({ sampleRate: 8000 });
            audioStartTimeRef.current = 0;
        }
        if (audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume().catch(() => {});
        }
    }, []);

    const playAudioChunk = useCallback((pcmData) => {
        if (!audioCtxRef.current || !isAuditingRef.current) return;
        
        const ctx = audioCtxRef.current;
        const buffer = ctx.createBuffer(1, pcmData.length, ctx.sampleRate);
        buffer.getChannelData(0).set(pcmData);
        
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        
        // Simple scheduling logic with drift correction
        const currentTime = ctx.currentTime;
        const drift = audioStartTimeRef.current - currentTime;
        
        // If audio is lagging more than 300ms or is too far in the past, reset to "now"
        if (drift > 0.3 || audioStartTimeRef.current < currentTime) {
            if (drift > 0.3) {
                console.debug(`[WebCodecs] Audio drift detected (${Math.round(drift * 1000)}ms). Resetting buffer for sync.`);
            }
            audioStartTimeRef.current = currentTime + 0.05; // 50ms buffer
        }
        
        source.start(audioStartTimeRef.current);
        audioStartTimeRef.current += buffer.duration;
    }, [isAuditing]);

    // --- Audio Lifecycle Control ---
    useEffect(() => {
        if (isAuditing) {
            try {
                if (!audioCtxRef.current) {
                    initAudio();
                } else if (audioCtxRef.current.state === 'suspended') {
                    audioCtxRef.current.resume().catch(() => {});
                }
            } catch (e) {
                // iOS Safari blocks AudioContext creation without a user gesture.
                // Silently ignore — video continues unaffected.
                console.warn('[WebCodecs] AudioContext init blocked (requires user gesture on iOS):', e.name);
            }
        } else {
            if (audioCtxRef.current && audioCtxRef.current.state === 'running') {
                audioCtxRef.current.suspend().catch(() => {});
            }
        }
    }, [isAuditing, initAudio]);

    // ── Main connect function ─────────────────────────────────────────────────
    const connect = useCallback(() => {
        if (!isMountedRef.current) return;
        if (!cameraId) return;

        if (videoEnabled && !('VideoDecoder' in window)) {
            if (!window.isSecureContext) {
                console.warn('[WebCodecs] VideoDecoder is not available in insecure contexts (HTTP). Please use HTTPS or localhost for WebCodecs support.');
            } else {
                console.warn('[WebCodecs] VideoDecoder API not available.');
            }
            setStatus('unsupported');
            return;
        }

        closeWS();
        closeDecoder();

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const params = new URLSearchParams();
        if (token) params.set('token', token);
        params.set('quality', quality); // 'sub' (mobile/grid) or 'main' (fullscreen/single)
        const wsUrl = `${protocol}//${window.location.host}/api/cameras/${cameraId}/ws?${params.toString()}`;

        let ws;
        try {
            ws = new WebSocket(wsUrl);
        } catch (e) {
            console.error('[WebCodecs] WebSocket construction failed:', e);
            setStatus('error');
            return;
        }
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => {
            console.debug(`[WebCodecs] WS connected for camera ${cameraId}`);
            // NOTE: retryCountRef is NOT reset here — only after the first real video
            // frame arrives (see onmessage below).  Resetting on WS-open alone would
            // create an infinite tight reconnect loop when the socket connects but the
            // engine is not yet streaming (e.g. camera offline, engine starting).
            lastFrameTimeRef.current = Date.now();
            wsOpenTimeRef.current = Date.now();
            lastDecodedFrameTimeRef.current = 0; // reset: new connection = new decoder stall window
            decoderErrorCountRef.current = 0;    // fresh connection = fresh error budget

            // Heartbeat: keep WS alive with a ping every 30s
            if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = setInterval(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    try { wsRef.current.send(JSON.stringify({ type: 'ping' })); } catch (_) {}
                }
            }, 30_000);

            // Watchdog: force reconnect if no video frame arrives for 10s
            if (watchdogIntervalRef.current) clearInterval(watchdogIntervalRef.current);
            watchdogIntervalRef.current = setInterval(() => {
                if (!isMountedRef.current) return;
                const now = Date.now();
                const elapsed = now - lastFrameTimeRef.current;

                // Primary stall: no WS video packets for 10s → reconnect
                if (lastFrameTimeRef.current > 0 && elapsed > 10_000) {
                    console.warn(`[WebCodecs] Watchdog: no WS video for ${Math.round(elapsed / 1000)}s on camera ${cameraId}. Reconnecting.`);
                    clearInterval(watchdogIntervalRef.current);
                    watchdogIntervalRef.current = null;
                    clearInterval(heartbeatIntervalRef.current);
                    heartbeatIntervalRef.current = null;
                    lastFrameTimeRef.current = 0;
                    setStatus('connecting');
                    const stalled = wsRef.current;
                    wsRef.current = null;
                    if (stalled) {
                        stalled.onopen = stalled.onclose = stalled.onerror = stalled.onmessage = null;
                        try { stalled.close(); } catch (_) {}
                    }
                    retryTimerRef.current = setTimeout(() => {
                        if (isMountedRef.current) connect();
                    }, 1_000);
                    return;
                }

                // Silent decoder stall: WS is receiving video but the decoder never outputs
                // a frame. Happens on mobile when the hardware decoder silently rejects the
                // codec profile/level (e.g. H.264 High Profile Level 5.x on iOS/Android).
                // Give 15s from WS open before declaring a stall to accommodate long GOPs.
                const wsAge = now - wsOpenTimeRef.current;
                const wsHasVideo = lastFrameTimeRef.current > wsOpenTimeRef.current;
                if (wsHasVideo && lastDecodedFrameTimeRef.current === 0 && wsAge > 15_000) {
                    console.warn(
                        `[WebCodecs] Decoder silent for ${Math.round(wsAge / 1000)}s despite receiving WS video` +
                        ` on camera ${cameraId} — codec may be unsupported on this device. Falling back to JPEG.`
                    );
                    clearInterval(watchdogIntervalRef.current);
                    watchdogIntervalRef.current = null;
                    clearInterval(heartbeatIntervalRef.current);
                    heartbeatIntervalRef.current = null;
                    setStatus('error'); // triggers JPEG fallback via handleWebCodecsState
                }
            }, 5_000);
        };

        ws.onmessage = (event) => {
            if (!isMountedRef.current) return;

            const buffer = event.data;
            if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 11) return;

            try {
                const view = new DataView(buffer);
                const pType = view.getUint8(0); // 0=Video, 1=Audio
                const isKeyframe = view.getUint8(1) === 1;
                const tsSec = view.getFloat64(2, true); // little-endian
                const tsUs = Math.floor(tsSec * 1_000_000);
                
                if (pType === 0) { // VIDEO
                    lastFrameTimeRef.current = Date.now();
                    // Reset retry counter only once we have confirmed live video data.
                    // This prevents WS-connect-with-no-video from resetting the backoff.
                    retryCountRef.current = 0;
                    const naluBytes = new Uint8Array(buffer, 10);

                    if (!isReadyRef.current) {
                        if (!isKeyframe) return;
                        isReadyRef.current = true;
                    }

                    if (!videoEnabled) return;

                    // ── Deferred codec configuration ──
                    if (isKeyframe) {
                        const detected = detectCodecFromSPS(naluBytes);
                        const codec = detected || 'avc1.42E01E';
                        if (!ensureDecoderConfigured(codec)) {
                            console.error('[WebCodecs] Cannot configure decoder — aborting.');
                            setStatus('error');
                            return;
                        }
                    }

                    if (!decoderRef.current || decoderRef.current.state !== 'configured') return;

                    decoderRef.current.decode(new EncodedVideoChunk({
                        type: isKeyframe ? 'key' : 'delta',
                        timestamp: tsUs,
                        data: naluBytes,
                        // duration intentionally omitted — some WebKit versions reject 0
                    }));
                } else if (pType === 1) { // AUDIO
                    const pcmALaw = new Uint8Array(buffer, 10);
                    const pcmLinear = new Float32Array(pcmALaw.length);
                    for (let i = 0; i < pcmALaw.length; i++) {
                        pcmLinear[i] = alaw2linear(pcmALaw[i]);
                    }
                    try {
                        playAudioChunk(pcmLinear);
                    } catch (audioErr) {
                        console.warn('[WebCodecs] Audio playback skipped to protect video stream:', audioErr);
                    }
                } else if (pType === 2) { // METADATA (JSON)
                    const jsonBytes = new Uint8Array(buffer, 10);
                    const jsonStr = new TextDecoder().decode(jsonBytes);
                    try {
                        const metadata = JSON.parse(jsonStr);
                        latestMetadataRef.current = metadata;
                        metadataTimeRef.current = Date.now();
                    } catch (jsonErr) {
                        console.warn('[WebCodecs] Failed to parse metadata:', jsonErr);
                    }
                }

            } catch (err) {
                console.error('[WebCodecs] Packet handling error:', err);
            }
        };

        ws.onerror = (e) => {
            // onclose always fires after onerror — retry logic lives there
            console.warn(`[WebCodecs] WS error for camera ${cameraId}:`, e.type, e);
        };

        ws.onclose = (e) => {
            if (!isMountedRef.current) return;
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
            clearInterval(watchdogIntervalRef.current);
            watchdogIntervalRef.current = null;
            console.warn(`[WebCodecs] WS closed for camera ${cameraId}: code=${e.code} reason="${e.reason}" wasClean=${e.wasClean}`);

            if (e.code === 1008) {
                setStatus('unauthorized');
                return;
            }

            const attempt = retryCountRef.current;
            if (attempt < MAX_RETRIES) {
                const delay = Math.min(RETRY_BASE_MS * (2 ** attempt), 30_000);
                retryCountRef.current += 1;
                console.debug(`[WebCodecs] Retry ${attempt + 1}/${MAX_RETRIES} in ${delay}ms…`);
                setStatus('connecting');
                retryTimerRef.current = setTimeout(() => {
                    if (isMountedRef.current) connect();
                }, delay);
            } else {
                console.warn(`[WebCodecs] Exhausted retries for camera ${cameraId}. Triggering MJPEG fallback.`);
                setStatus('error');
            }
        };

    }, [cameraId, token, videoEnabled, quality, closeWS, closeDecoder, ensureDecoderConfigured]);

    // ── Mount / unmount ───────────────────────────────────────────────────────
    useEffect(() => {
        isMountedRef.current = true;
        retryCountRef.current = 0;
        decoderErrorCountRef.current = 0;
        connect();

        return () => {
            isMountedRef.current = false;
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
            if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
            if (watchdogIntervalRef.current) clearInterval(watchdogIntervalRef.current);
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            closeWS();
            closeDecoder();
            if (audioCtxRef.current) {
                try { audioCtxRef.current.close(); } catch (_) {}
                audioCtxRef.current = null;
            }
        };
    }, [cameraId, token, videoEnabled, connect, closeWS, closeDecoder]);

    return (
        <div className="absolute inset-0">
            {videoEnabled && (
                <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full object-contain"
                    style={{
                        display: status === 'loaded' ? 'block' : 'none'
                    }}
                />
            )}
            {camera.audio_enabled && status === 'loaded' && isAuditing && (
                <div className="absolute top-2 right-2 z-50 p-2 bg-primary/20 backdrop-blur-md rounded-full border border-primary/30 animate-in fade-in zoom-in duration-300">
                    <Volume2 className="w-4 h-4 text-primary animate-pulse" />
                </div>
            )}
        </div>
    );
};
